import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api, { setActiveSession, setInMemoryToken, clearInMemoryToken, refreshSession } from "@/lib/api";
import { claimIdentity } from "@/lib/tracker";
import { trackEvent, identifyUser, resetUser } from "@/hooks/useTracking";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [sessionChecked, setSessionChecked] = useState(false);
  const loggedInRef = useRef(false);

  // On mount, hydrate auth state. We call /auth/refresh (NOT /auth/session)
  // because refresh handles every cookie state in one round-trip:
  //   • valid access + valid refresh   → rotates both, returns user (✅)
  //   • expired access + valid refresh → rotates both, returns user (✅)
  //   • legacy access-only cookie (pre-Phase-118 logged-in users) → upgrades
  //     to access+refresh pair, returns user (✅ zero-downtime migration)
  //   • all missing/expired           → 401, user genuinely logged out
  // This eliminates the "logged out on tab refresh" bug where legacy users
  // would silently stay on access-only and get kicked out the moment that
  // access cookie expired (no refresh cookie ever issued).
  useEffect(() => {
    let cancelled = false;

    async function hydrateUserFromBody(body) {
      if (cancelled || !body?.authenticated) return false;
      const u = body.user;
      setUser(u);
      setRole(u?.role || body.role || "");
      loggedInRef.current = true;
      setActiveSession(true);
      if (body.token) setInMemoryToken(body.token);
      try { localStorage.setItem("nc_had_session", "1"); } catch {}
      return true;
    }

    async function attemptRefresh(retryOnTransient = true) {
      try {
        const fresh = await refreshSession();
        return await hydrateUserFromBody(fresh);
      } catch (err) {
        const status = err?.response?.status;
        // 401 = genuinely no valid session → user must re-login. Anything
        // else (5xx, 0/network, timeout) is transient — DO NOT log the user
        // out. Retry once after 1.5s; if still failing, fall back to
        // /auth/session (read-only, idempotent) so an ephemeral backend
        // hiccup during a deploy doesn't kick admins to /admin/login.
        if (status === 401) {
          // Clear the session hint so anonymous visitors don't keep
          // hitting refresh on every reload (Phase 122d — silences the
          // 401 console noise flagged in the post-deploy smoke-run).
          try { localStorage.removeItem("nc_had_session"); } catch {}
          return false;
        }
        if (retryOnTransient) {
          await new Promise((r) => setTimeout(r, 1500));
          return attemptRefresh(false);
        }
        // Last-ditch read-only check using /auth/session — if access cookie
        // still works, keep the user logged in even though refresh is
        // currently flaky. Next user action will retry refresh.
        try {
          const res = await api.get("/auth/session");
          return await hydrateUserFromBody(res.data);
        } catch {
          return false;
        }
      }
    }

    async function bootSession() {
      try {
        // Skip the refresh round-trip entirely for first-time / anonymous
        // visitors. Refresh/access tokens are httpOnly so JS can't peek at
        // them — we use a localStorage hint `nc_had_session` set on any
        // prior successful login. Absent hint = definitely-never-logged-in
        // on this browser, so we save the 401 + console-noise.
        let hadSession = false;
        try { hadSession = localStorage.getItem("nc_had_session") === "1"; } catch {}
        if (!hadSession) {
          return;
        }
        await attemptRefresh(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSessionChecked(true);
        }
      }
    }
    bootSession();
    return () => { cancelled = true; };
  }, []);

  // Pre-emptive token refresh — keep the access token alive for active users.
  // Runs every 30 minutes (access token TTL is 60 min, so we refresh before
  // expiry). Uses the single-flight refresh helper so a failure here can NEVER
  // dispatch logout — only an explicit user action against a hard-auth
  // endpoint will. Skips refresh when the tab is hidden (browsers throttle
  // background timers + the next user action will refresh on demand anyway).
  useEffect(() => {
    if (!user) return;
    const tick = async () => {
      if (document.visibilityState === "hidden") return;
      try { await refreshSession(); } catch { /* swallow — never logout from here */ }
    };
    const interval = setInterval(tick, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // Listen for forced logout from api interceptor
  useEffect(() => {
    const handleLogout = () => {
      if (loggedInRef.current) {
        loggedInRef.current = false;
        setActiveSession(false);
        clearInMemoryToken();
        setUser(null);
        setRole("");
      }
    };
    window.addEventListener("nc_logout", handleLogout);
    return () => window.removeEventListener("nc_logout", handleLogout);
  }, []);

  const login = useCallback((tokenStr, roleStr, userData) => {
    // Store token in memory (NOT localStorage) for this session
    if (tokenStr) {
      setInMemoryToken(tokenStr);
    }
    setRole(roleStr);
    setUser(userData);
    loggedInRef.current = true;
    setActiveSession(true);
    setLoading(false);
    setSessionChecked(true);
    localStorage.removeItem("nc_needs_phone");
    // Hint used on cold-load to decide whether to call /auth/refresh.
    try { localStorage.setItem("nc_had_session", "1"); } catch {}
    if (userData?.id) {
      identifyUser(userData.id, {
        email: userData.email,
        name: userData.name,
        role: roleStr,
      });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Silent
    }
    loggedInRef.current = false;
    setActiveSession(false);
    clearInMemoryToken();
    localStorage.removeItem("nc_needs_phone");
    try { localStorage.removeItem("nc_had_session"); } catch {}
    setUser(null);
    setRole("");
    resetUser();
  }, []);

  const requestOTP = useCallback(async (phone) => {
    const res = await api.post("/auth/otp/request", { phone });
    return res.data;
  }, []);

  const verifyOTP = useCallback(async (phone, otp) => {
    const res = await api.post("/auth/otp/verify", { phone, otp });
    login(res.data.token, res.data.role, res.data.user);
    if (res.data.user?.id) {
      claimIdentity(res.data.user.id, phone, null, 'sms');
      trackEvent('signup_completed', { method: 'phone', user_id: res.data.user.id });
    }
    return res.data;
  }, [login]);

  const adminLogin = useCallback(async (email, password) => {
    const res = await api.post("/auth/admin/login", { email, password });
    login(res.data.token, res.data.role, res.data.user);
    return res.data;
  }, [login]);

  const riderLogin = useCallback(async (email, password) => {
    const res = await api.post("/auth/rider/login", { email, password });
    login(res.data.token, res.data.role, res.data.user);
    return res.data;
  }, [login]);

  const isAuthenticated = !!user;
  const isAdmin = ["OWNER", "ADMIN", "OPS_MANAGER", "CATALOG_MANAGER", "SUPPORT", "FINANCE"].includes(role);
  const isRider = role === "RIDER";
  const isCustomer = role === "CUSTOMER";

  return (
    <AuthContext.Provider value={{
      user, token: null, role, loading, isAuthenticated, isAdmin, isRider, isCustomer,
      sessionChecked,
      login, logout, requestOTP, verifyOTP, adminLogin, riderLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
